import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const CMS_FAQ_CATEGORIES = '/cms/faq-categories';

/**
 * List FAQ categories. Returns { data, loading, error, refetch }.
 */
export function useFaqCategoriesList(params = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(CMS_FAQ_CATEGORIES, {
        params: {
          lang: params.lang ?? 'en',
          includeFaqs: params.includeFaqs ? 'true' : undefined,
          status: params.status,
        },
      });
      setData(res?.data?.faqCategories ?? []);
      return res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load FAQ categories';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.lang, params.includeFaqs, params.status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * Create FAQ category. Returns { execute, loading, error }.
 * payload = { sortOrder?, status?, descriptions: [{ languageCode?, title, description? }] }
 */
export function useCreateFaqCategory() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(CMS_FAQ_CATEGORIES, payload);
      return res?.data?.faqCategory ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create category';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Update FAQ category. Returns { execute, loading, error }.
 * execute(id, payload), payload = { sortOrder?, status?, parentId?, descriptions? }
 *
 * `descriptions` replaces what the category has, so send the title along with
 * the description even when only the description changed — otherwise the title
 * is replaced with an empty one.
 */
export function useUpdateFaqCategory() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    if (!id) throw new Error('FAQ category id is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${CMS_FAQ_CATEGORIES}/${id}`, payload);
      return res?.data?.faqCategory ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update category';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * Delete FAQ category. Returns { execute, loading, error }.
 * execute(id)
 */
export function useDeleteFaqCategory() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('FAQ category id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${CMS_FAQ_CATEGORIES}/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete category';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}
